import cx from 'classnames';
import CallActive from 'Components/CallActive';
import CallHoldingList from 'Components/CallHoldingList';
import CallIncomingList from 'Components/CallIncomingList';
import { TransferDirectory } from 'Components/TransferDirectory';
import TransferHoldingList from 'Components/TransferHoldingList';
import {
  STORE_CONVERSATION,
  STORE_PARTICIPANT,
  STORE_PERSON,
  STORE_PHONE_CALL,
  STORE_PUSHER,
  STORE_UI,
} from 'Constants/stores';
import { useNetworkStatus } from 'Hooks/network';
import { isEmpty } from 'lodash';
import { inject, observer } from 'mobx-react';
import { SoftphoneCoordinator } from 'Modules/softphone/utils/softphoneCoordinator';
import React, { useEffect, useRef } from 'react';
import { Grid } from 'semantic-ui-react';
import IncomingVideoCall from '../../components/IncomingVideoCall';
import { Styled } from './index.styles';
import { ICallSidebarProps } from './interface';

const CallSidebar = (props: ICallSidebarProps): React.ReactElement => {
  const {
    participant,
    person,
    phoneCall: phoneStore,
    ui,
    conversation,
    isFixed,
    hidden,
  } = props;

  const { isOnline } = useNetworkStatus();
  const incomingHoldingWrapper = useRef(null);

  const activeCall = phoneStore.AnyPhoneConnectionActive;
  const largeColumns = 4;
  const smallColumns = !activeCall ? 4 : 6;

  const isInternetDropped: boolean =
    (!isOnline &&
      phoneStore.ActivePhoneCall?.isCallConnected &&
      !phoneStore.isCallTimeout) ||
    !phoneStore.isWebRTCConnected;

  const showConnectionLossFeedback: boolean =
    phoneStore.isConnectionLossFeedbackActive && isInternetDropped;

  useEffect(() => {
    if (isOnline && phoneStore.isWebRTCConnected) {
      void phoneStore?.clearConnectionLossFeedback();
    }

    if (!isOnline) {
      phoneStore.incomingPhoneCalls?.[0]?.incomingTone?.pause();

      phoneStore.ActivePhoneCall?.isCallConnecting &&
        phoneStore.ActivePhoneCall.ringtone.pause();

      phoneStore.ActivePhoneCall?.isCallConnected &&
        phoneStore.noInternetOnActiveCall();
    }

    if (!phoneStore.isWebRTCConnected) {
      phoneStore.incomingPhoneCalls?.[0]?.incomingTone?.pause();

      if (phoneStore.ActivePhoneCall?.isCallConnecting) {
        phoneStore.ActivePhoneCall.ringtone.pause();
        phoneStore.ActivePhoneCall.incomingTone.pause();
      }

      phoneStore.ActivePhoneCall?.isCallConnected &&
        !phoneStore.isCallTimeout &&
        phoneStore.noInternetOnActiveCall();
    }
  }, [
    isOnline,
    phoneStore,
    phoneStore.isWebRTCConnected,
    phoneStore.incomingPhoneCalls,
    phoneStore.ActivePhoneCall,
    phoneStore.isCallTimeout,
  ]);

  const isActiveCall = () => {
    return (
      phoneStore.ActivePhoneCall &&
      !phoneStore.ActivePhoneCall.transferMode &&
      phoneStore.AnyPhoneConnectionActive
    );
  };

  const isTransferDirectory = () => {
    return (
      phoneStore.ActivePhoneCall &&
      phoneStore.AnyPhoneConnectionActive &&
      phoneStore.ActivePhoneCall.transferMode &&
      phoneStore.isWebRTCConnected &&
      isOnline &&
      !phoneStore.isCallTimeout
    );
  };

  const isTransferOrHoldCallActive = () => {
    return (
      phoneStore.ActivePhoneCall &&
      phoneStore.AnyPhoneConnectionActive &&
      phoneStore.isWebRTCConnected &&
      isOnline
    );
  };

  const isBlindTransfer = () => {
    return (
      phoneStore.ActivePhoneCall &&
      phoneStore.ActivePhoneCall.transferMode &&
      !phoneStore.ActivePhoneCall.isWarmTransfer &&
      phoneStore.isWebRTCConnected &&
      isOnline &&
      !phoneStore.isCallTimeout
    );
  };

  const isWarmTransfer = () => {
    return (
      phoneStore.ActivePhoneCall &&
      phoneStore.ActivePhoneCall.transferMode &&
      phoneStore.ActivePhoneCall.isWarmTransfer &&
      phoneStore.isWebRTCConnected &&
      isOnline &&
      !phoneStore.isCallTimeout
    );
  };

  const isCallInHold = () => {
    return (
      phoneStore.ActivePhoneCall &&
      !phoneStore.ActivePhoneCall.transferMode &&
      phoneStore.phoneCalls.length > 1 &&
      phoneStore.isWebRTCConnected &&
      isOnline
    );
  };

  const isIncomingCall = () => {
    return (
      phoneStore.incomingPhoneCalls.length > 0 &&
      phoneStore.isWebRTCConnected &&
      isOnline
    );
  };

  // Softphone Coordination: Only show legacy video calls if new softphone is not active
  const coordinator = SoftphoneCoordinator.getInstance();
  const personId = person.loggedInPersonId;
  const isNewSoftphoneEnabled = coordinator.readPreferenceSync(personId);

  const isIncomingVideoCall =
    !isNewSoftphoneEnabled && conversation.listOfIncomingVideoCalls.size > 0;

  const showCallSidebar =
    phoneStore.AnyPhoneConnectionActive ||
    phoneStore.isCallTimeout ||
    isIncomingVideoCall ||
    phoneStore.isAttemptingToReconnectSip ||
    phoneStore.isWebrtcAttemptDone;

  return (
    <Grid.Column
      icon="labeled"
      widescreen={largeColumns}
      computer={largeColumns}
      desktop={largeColumns}
      tablet={smallColumns}
      mobile={smallColumns}
      className={cx('nogrow call-sidebar', {
        visible: showCallSidebar,
        hidden: !showCallSidebar || hidden,
        'softphone--fixed softphone--fixed': isFixed,
        '!w-auto !basis-auto !shadow-none !border-none': isFixed,
      })}
    >
      {
        // Active Call
        isActiveCall() && (
          <div
            className={cx('call-active-wrapper', {
              'calling-dialpad-opened': phoneStore.ActivePhoneCall.showDialPad,
            })}
          >
            <CallActive
              isOnline={isOnline}
              isConnectionLostFeedbackVisible={showConnectionLossFeedback}
              disabledActions={isInternetDropped}
              loadOrCreateConversationWithPost={
                conversation.loadOrCreateConversationWithPost
              }
              loggedInAccountId={person.loggedInAccountId}
              loggedInUserActiveConferenceConversation={
                conversation.LoggedInUserActiveConferenceConversation
              }
              postConferenceByConversationId={
                conversation.postConferenceByConversationId
              }
              getExtrContactByPhoneNumber={person.getExtrContactByPhoneNumber}
              setConversationAndTotalUnreadCount={
                ui.setConversationAndTotalUnreadCount
              }
              updateMyLastReadMessage={participant.updateMyLastReadMessage}
            />
          </div>
        )
      }

      {showConnectionLossFeedback && (
        <Styled.Overlay $isCentered={isWarmTransfer()} $isFixed={isFixed}>
          <Styled.OverlayTitle>
            <Styled.WarningIcon />
            You have lost connection
          </Styled.OverlayTitle>
          <Styled.OverlayDescription>
            Trying to reconnect…
          </Styled.OverlayDescription>
        </Styled.Overlay>
      )}

      {
        // Transfer Directory
        isTransferDirectory() && (
          <div className="transfer-component">
            <TransferDirectory />
          </div>
        )
      }
      {isTransferOrHoldCallActive() && (
        <div
          ref={incomingHoldingWrapper}
          className={cx('incoming-holding-wrapper', {
            'active-half':
              phoneStore.ActivePhoneCall &&
              phoneStore.incomingPhoneCalls.length,
            'transfer-open': phoneStore.TransferMode,
            'incoming-call': phoneStore.incomingPhoneCalls.length,
            'single-call-on-hold': phoneStore.phoneCalls.length === 2,
            'multiple-calls-on-hold': phoneStore.phoneCalls.length >= 3,
          })}
        >
          {
            // Transfer Holding List
            isBlindTransfer() && (
              <TransferHoldingList
                callHoldlist={[phoneStore.callBeingTransfered]}
              />
            )
          }
          {
            // Transfer Holding List
            isWarmTransfer() && (
              <TransferHoldingList
                callHoldlist={[phoneStore.warmTransferOld]}
              />
            )
          }
          {
            // Call Holding List
            isCallInHold() && (
              <CallHoldingList
                phoneStore={phoneStore}
                phoneCallsList={phoneStore.phoneCalls}
              />
            )
          }
        </div>
      )}
      {
        // Incoming Calls
        isIncomingCall() && (
          <CallIncomingList
            person={person}
            ui={ui}
            phone={phoneStore}
            incomingCalls={phoneStore.incomingPhoneCalls}
            activeCallExists={!isEmpty(phoneStore.ActivePhoneCall)}
          />
        )
      }

      {isIncomingVideoCall && <IncomingVideoCall />}
    </Grid.Column>
  );
};

export default inject(
  STORE_PHONE_CALL,
  STORE_UI,
  STORE_CONVERSATION,
  STORE_PERSON,
  STORE_PARTICIPANT,
  STORE_PUSHER
)(observer(CallSidebar));
